// react
import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
// form handling
import {Form, FormikProvider, useFormik} from 'formik';
// material
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Card,
	Chip,
	Container,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
// components
import AddCategoryModal from 'src/components/modals/AddCategoryModal';
import DeleteAlertDialog from 'src/components/modals/DeleteAlertDialog.js';
import Dropzone from '../../components/inputs/dropzone';
// api
import {deleteProduct, getProduct, updateProduct} from '../../utils/AdminApi';

//redux
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import AddPropertyValueModal from 'src/components/_dashboard/properties/AddPropertyValueModal';
import AccessControlWrapper from 'src/components/AccessControlWrapper';
import SubmitButton from 'src/components/buttons/SubmitButton';
import CustomFields from 'src/components/inputs/CustomFields';
import ImageCropPicker from 'src/components/inputs/ImageCropPicker.js/ImageCropPicker';
import Picker from 'src/components/inputs/Picker';
import NavButton from 'src/components/NavButton';
import Page from 'src/components/Page';
import {ScrollToFieldError} from 'src/hooks/ScrollToFieldError';
import useCategories from 'src/hooks/useCategories';
import DiscountPicker from 'src/pages/products/common/DiscountPicker';
import PublishSwitch from 'src/pages/products/common/PublishSwitch';
import {
	addProductPermission,
	AddProductsQuantityPermission,
	deleteProductPermission,
	findOneProductPermission,
	updateProductPermission,
	UpdateProductsQuantityPermission,
} from 'src/permissionsList';
import {isNullOrEmpty} from 'src/utils/helperMethods';
import {canAccess} from 'src/utils/permissions';
import AvailabilityRadioButtons from './common/AvailabilityRadioButtons';
import VariationsForm from './create/VariationsForm';
//query
import {useQuery, useQueryClient} from 'react-query';
import Dev from 'src/components/Dev';
import LinkToInventory from 'src/components/LinkToInventory';
import LinkToReviews from 'src/components/LinkToReviews';
import {printProductPrice} from 'src/features/printer';
import {useModal} from 'src/hooks/useModal';
import useUser from 'src/hooks/useUser';
import ProductBranchesAvailability from 'src/pages/products/common/ProductBranchesAvailability';
import {devLog} from 'src/utils/devUtils';
import {getNumber} from 'src/utils/numberUtils';
import {getImageSrc} from 'src/utils/productUtils';
import {accountIs} from 'src/utils/siteUtils';
import AddQuantityButton from './common/AddQuantityButton';
import UpdateProductQtyModal from './common/UpdateProductQtyModal';
import {updateProductSchema} from './product.schema';
import ProductBarcodeGenerator from './ProductBarcodeGenerator';
import useProductProperties from './useProductProperties';
import ProductPropertiesSelection from './ProductPropertiesSelection';
import {LoadingView} from 'src/routes';
import {ErrorView} from 'src/ErrorView';

export default function UpdateProduct() {
	const queryClient = useQueryClient();

	const navigate = useNavigate();
	const [selectedImages, setSelectedImages] = useState([]);
	const [categoryModalOpen, setCategoryModalOpen] = useState(false);
	const {categories} = useCategories();
	const {host} = useSelector((state) => state.admin.user.OwnedWebsite);

	const {user} = useUser();
	const branch = user.branch;

	let {id} = useParams();
	const canSubmit = canAccess(updateProductPermission);

	const {
		data: product,
		error,
		isLoading,
		refetch,
	} = useQuery(
		['product', id],
		() => {
			return getProduct(id);
		},
		{
			refetchOnWindowFocus: false,
		},
	);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			...product,
			variations: product?.variations ?? [],
			attrs: product?.attrs ?? [],
			inputs: product?.inputs ?? [],
			custom_fields: product?.custom_fields ?? {},
			categories: product?.categories?.map((cat) => cat.id) ?? [],
			mainImage: null,
		},
		validationSchema: updateProductSchema,
		onSubmit: async (v) => {
			devLog('update product onSubmit', v);
			if (!canSubmit) return;

			const {mainImage, variations, stocks, derived, created_at, updated_at, ...p} = v;

			if (variations.length && variations.every((v) => v.deleted)) {
				return toast.error('لا يمكن حذف كل خيارات المنتج');
			}

			await toast.promise(
				updateProduct(
					id,
					{
						...p,
						variations: variations.map((v) => ({
							id: v.id,
							name: v.name,
							values: v.values.map((v) => v.id),
							product: product.id,
							price: !isNullOrEmpty(v.price) ? v.price : null /* we want to store as null not 0 or "" */,
							discount: !isNullOrEmpty(v.discount)
								? v.discount
								: null /* we want to store as null not 0 or "" */,
							// quantity: isNullOrEmpty(v.quantity) ? 0 : v.quantity,
							cost_per_unit: v.cost_per_unit,
							addedQuantity: v.addedQuantity,

							isDeleted: v.deleted,
							branch: v.branch,
						})),
					},
					mainImage,
					selectedImages,
				),
				{
					error: 'حدث خطا غير متوقع',
					success: 'تم تحديث المنتج بنجاح',
					pending: 'يرجي الانتظار',
				},
			);
			// refetch product data from api to fetch updates
			const res = await refetch();
		},
	});

	const {
		errors,
		touched,
		values,
		isSubmitting,
		handleSubmit,
		getFieldProps,
		setFieldTouched,
		setFieldValue,
	} = formik;

	const {open, close} = useModal();
	const getHelperProps = (attr) => ({
		error: Boolean(touched[attr] && errors[attr]),
		helperText: touched[attr] && errors[attr],
		onWheel: (e) => e.target.blur(),
	});

	if (isLoading) return <LoadingView />;
	if (error) return <ErrorView error={error} onRefresh={refetch} />;

	const isVariableProduct = product.variations.length > 0;

	return (
		<Page title="تعديل المنتج">
			<AccessControlWrapper controllerAction={updateProductPermission} or={findOneProductPermission}>
				<Container maxWidth="md">
					<FormikProvider value={formik}>
						<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
							<Stack sx={{mb: 2}} spacing={3}>
								<Stack
									// sx={{mb: 4}}
									direction="row"
									justifyContent="space-between"
									alignItems="center"
								>
									<Stack>
										<Typography variant="h4">تعديل المنتج</Typography>
										<a
											style={{color: '#02393E'}}
											target="_blank"
											href={`https://${host}/products/${id}`}
											rel="noreferrer"
										>
											عرض المنتج
										</a>
									</Stack>

									<Stack direction="row">
										<PublishSwitch
											value={values.publish_at != null}
											onChange={(newValue) => setFieldValue('publish_at', newValue)}
										/>
										{canAccess(deleteProductPermission) && (
											<DeleteAlertDialog
												onDelete={async () => {
													try {
														await toast.promise(deleteProduct(product.id), {
															error: 'حدث خطأ غير متوقع',
															success: 'تم حذف المنتج بنجاح',
															pending: 'يرجي الانتظار',
														});
														navigate('/products', {replace: true});
													} catch (err) {
														//
													}
												}}
											/>
										)}
									</Stack>
								</Stack>

								<Stack spacing={3}>
									{/* Main info Card */}
									<Card sx={{p: 4, flex: 1}}>
										<Stack spacing={2}>
											<ScrollToFieldError />

											<Stack
												direction={{md: 'row', xs: 'column-reverse'}}
												// alignItems={{md: 'flex-start', xs: 'stretch'}}
												spacing={3}
											>
												{product && (
													<ImageCropPicker
														maxSize={3}
														placeholder={'الصورة الرئيسية'}
														defaultImage={getImageSrc(product)}
														{...getHelperProps('mainImage')}
														setFile={(file) => formik.setFieldValue('mainImage', file)}
													/>
												)}
												<Stack flex={1} justifyContent={'space-between'} spacing={2}>
													<TextField
														fullWidth
														type="text"
														label="اسم المنتج"
														{...getFieldProps('name')}
														{...getHelperProps('name')}
														value={values?.name ? values.name : ''}
													/>

													<TextField
														fullWidth
														type="text"
														minRows={4}
														multiline
														label="تفاصيل المنتج"
														{...getFieldProps('details')}
														{...getHelperProps('details')}
														value={values?.details ? values.details : ''}
													/>
												</Stack>
											</Stack>

											<Stack direction="row" spacing={1} alignItems="end">
												<Picker
													size="small"
													multiple
													{...getFieldProps('categories')}
													{...getHelperProps('categories')}
													label="التصنيف"
													options={categories}
													labelKey="name"
													valueKey="id"
													value={values.categories}
													onChange={(e) => {
														setFieldValue('categories', e.target.value);
													}}
													sx={{flex: 1}}
												/>
												<Button size="small" onClick={() => setCategoryModalOpen(true)}>
													إضافة
												</Button>
											</Stack>

											{/* <AvailabilityRadioButtons
												value={values.infinite}
												onChange={(newValue) => setFieldValue('infinite', newValue)}
											/> */}
											<Typography>نوع المنتج: {values.infinite ? 'متوفر بالحجز' : 'استلام فوري'}</Typography>

											{/* quantity update */}
											{product && isNullOrEmpty(product?.variations) && !values.infinite && (
												<Stack spacing={0}>
													<Stack px={1} py={1} border={'1px solid #dbdee0'} borderRadius={1} spacing={2}>
														<Stack direction={'row'} spacing={1} alignItems={'center'}>
															<Typography flexGrow={1}>الكمية: {product.derived.branch_quantity}</Typography>

															{canAccess(AddProductsQuantityPermission) && (
																<AddQuantityButton productId={product.id} refetch={refetch} />
															)}
														</Stack>
													</Stack>
													{canAccess(UpdateProductsQuantityPermission) && (
														<Button
															onClick={(e) =>
																open({
																	title: 'تعديل الكمية',
																	children: (
																		<UpdateProductQtyModal
																			onSuccess={() => {
																				refetch();
																				close();
																			}}
																			productId={product.id}
																		/>
																	),
																})
															}
															sx={{
																color: 'gray',
																display: 'inline-flex',
																justifyContent: 'center',
																alignItems: 'center',
																width: 'auto',
																minWidth: 'unset',
																flex: 'none',
																alignSelf: 'flex-end',
																fontSize: '9px',
																mt: 0, // Ensures no extra spacing
																padding: '4px 8px', // Adjusts button size
															}}
														>
															تصحيح الكمية
														</Button>
													)}
													{product && <ProductBranchesAvailability product={product} refetch={refetch} />}
												</Stack>
											)}

											<Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
												<TextField
													fullWidth
													type="text"
													pattern="\d*"
													InputProps={{min: 0}}
													label="السعر"
													{...getFieldProps('price')}
													{...getHelperProps('price')}
													value={values?.price != null ? values.price : ''}
												/>
												<DiscountPicker
													sx={{maxWidth: 90}}
													name="discount"
													label="التخفيض"
													value={values.discount ?? 0}
													onChange={(discountValue) => {
														setFieldValue('discount', discountValue);
													}}
												/>
												<IconButton size="small" onClick={() => printProductPrice(values)}>
													<PrintRoundedIcon fontSize="small" />
												</IconButton>
											</Stack>

											{isVariableProduct && (
												<Typography variant="caption">
													سيتم استخدام هذا السعر في حالة عدم تحديد السعر لكل خيار
												</Typography>
											)}

											<LinkToInventory productId={id} variationId={product.variations[0]?.id} />
											<LinkToReviews productId={id} />
										</Stack>
									</Card>
									{/* VARIATIONS CARD */}
									{isVariableProduct && (
										<Card sx={{p: 4, flex: 1}}>
											<ProductPropertiesSelection
												variations={values.variations}
												setVariations={(newVariations) => setFieldValue('variations', newVariations)}
											/>
											<VariationsForm
												showSort
												variations={values.variations}
												setVariations={(variations) => setFieldValue('variations', variations)}
												values={values}
												productId={product.id}
												refetch={() => {
													refetch();
												}}
											/>
										</Card>
									)}
									<Card sx={{p: 4, flex: 1}}>
										<Accordion>
											<AccordionSummary expandIcon={<ExpandMoreIcon />}>
												<Typography>معلومات اضافية</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<Stack spacing={2}>
													<Typography>صور اضافية للمنتج</Typography>
													{product && (
														<Dropzone
															maxSize={1}
															allowMultiple
															images={values?.images}
															onImagesChanged={(images) => {
																setFieldValue('images', images);
															}}
															selectedFiles={selectedImages}
															onFilesChanged={setSelectedImages}
														/>
													)}
													<CustomFields
														title={'جدول المواصفات '}
														errors={errors.attrs}
														touches={touched.attrs}
														value={values?.attrs ?? []}
														onChange={(newValue) => formik.setFieldValue('attrs', newValue)}
														setFieldTouched={setFieldTouched}
													/>

													<TextField
														fullWidth
														InputProps={{min: 0}}
														pattern="\d*"
														type="text"
														inputMode="numeric"
														label="الوزن"
														placeholder="الوزن بالكيلو"
														{...getFieldProps('weight')}
														{...getHelperProps('weight')}
														value={values.weight ? values.weight : ''}
													/>
													<Stack spacing={1} direction="row" justifyContent="space-between">
														<TextField
															fullWidth
															InputProps={{min: 0}}
															pattern="\d*"
															type="text"
															inputMode="numeric"
															label="الطول"
															placeholder="الطول"
															{...getFieldProps('width')}
															{...getHelperProps('width')}
															value={values.width ? values.width : ''}
														/>
														<TextField
															fullWidth
															InputProps={{min: 0}}
															pattern="\d*"
															type="text"
															inputMode="numeric"
															label="العرض"
															placeholder="العرض"
															{...getFieldProps('height')}
															{...getHelperProps('height')}
															value={values.height ? values.height : ''}
														/>
														<TextField
															fullWidth
															InputProps={{min: 0}}
															pattern="\d*"
															type="text"
															inputMode="numeric"
															label="الارتفاع"
															placeholder="الارتفاع"
															{...getFieldProps('_length')}
															{...getHelperProps('_length')}
															value={values._length ? values._length : ''}
														/>
													</Stack>

													{accountIs('alassalsweets', true) && (
														<CustomFields
															value={values.inputs}
															setFieldTouched={setFieldTouched}
															onChange={(v) => setFieldValue('inputs', v)}
															title={'حقول مخصصة'}
															inputKey="name"
															vertical
															maxInputs={1}
															allowChangeType
															fields={[
																{
																	label: 'اسم الحقل',
																	key: 'label',
																},
																{
																	label: 'اجباري',
																	type: 'checkbox',
																	key: 'required',
																},
															]}
															bgcolor={'#F3F3F3'}
															renderOptions={({options, handleInputChange}) => {
																return options.map((option) => {
																	return (
																		<Stack
																			spacing={1}
																			alignItems="center"
																			direction="row"
																			justifyContent="space-between"
																			key={option.value}
																		>
																			<Typography>{option.value}</Typography>
																			<TextField
																				type="text"
																				pattern="\d*"
																				sx={{width: 100}}
																				InputProps={{min: 0}}
																				label="السعر"
																				// {...getFieldProps('price')}
																				// {...getHelperProps('price')}
																				onChange={(e) =>
																					handleInputChange({...option, price: getNumber(e.target.value)})
																				}
																				value={option?.price ?? ''}
																			/>
																		</Stack>
																	);
																});
															}}
														/>
													)}

													{values.id && (
														<ProductBarcodeGenerator
															pid={product.id}
															sku={values?.sku}
															onChange={(v) => setFieldValue('sku', v)}
														/>
													)}

													{accountIs('crocotools', true) && (
														<Stack>
															<Typography>اضافات</Typography>
															<TextField
																label="vanex storage id"
																{...getFieldProps('custom_fields.vanex_storage_id')}
																{...getHelperProps('custom_fields.vanex_storage_id')}
																value={values.custom_fields?.vanex_storage_id ?? ''}
															/>
														</Stack>
													)}

													{accountIs('alassalsweets.com', true) && (
														<TextField
															label="الاسم بالانجليزي"
															{...getFieldProps('custom_fields.english_name')}
															{...getHelperProps('custom_fields.english_name')}
															value={values.custom_fields?.english_name ?? ''}
														/>
													)}

													{canAccess() && product?.creator && (
														<Typography>تم انشاء المنتج عن طريق: {product.creator?.name}</Typography>
													)}
												</Stack>
											</AccordionDetails>
										</Accordion>
									</Card>
								</Stack>

								{product && (
									<Dev
										title={'state'}
										object={{
											// values,
											// selectedVariations,
											quantity: values.quantity,
											derived: {
												quantity: product.derived.quantity,
												branch_quantity: product.derived.branch_quantity,
											},
											stocks: product.stocks
												.map((s) => {
													return {
														variation: s.variation,
														branch: s.branch,
														quantity: s.quantity,
													};
												})
												.sort((a, b) => a.branch - b.branch),
										}}
									/>
								)}
								{canSubmit && <SubmitButton isSubmitting={isSubmitting}>تحديث المعلومات</SubmitButton>}
								{canAccess(addProductPermission) && (
									<NavButton variant="outlined" to="/products/create" text="اضافة منتج اخر" />
								)}

								<AddCategoryModal
									open={categoryModalOpen}
									onClose={() => setCategoryModalOpen(false)}
									onCategoryCreated={(newCategory) => {
										// Add the new category to the currently selected categories
										const currentCategories = values.categories || [];
										setFieldValue('categories', [...currentCategories, newCategory.id]);
									}}
								/>
							</Stack>
						</Form>
					</FormikProvider>
				</Container>
			</AccessControlWrapper>
		</Page>
	);
}
